import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { resolveProfileImageUrl } from '../utils/profileImage'
import { getDemoWorkspaceData } from '../explore_seeds/ariaAndCoDemoData'
import { getPublicProfiles, type PublicProfile } from './publicProfileService'

export type WorkspaceUserDirectoryEntry = {
  membershipId: string
  uid: string
  displayName: string
  email: string
  role: string
  avatarUrl: string
  mode?: string
}

const stringValue = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const fallbackName = (uid: string, email: string) => {
  if (email && !email.includes('@')) return email
  if (email.includes('@')) return email.split('@')[0] || 'Workspace member'
  return uid ? `Member ${uid.slice(0, 6)}` : 'Workspace member'
}

export const resolveWorkspaceDirectoryDisplayName = (
  membership: Record<string, unknown>,
  profile: PublicProfile | undefined | null,
  fallback: string,
): string =>
  stringValue(profile?.displayName) ||
  stringValue(membership.displayName) ||
  stringValue(membership.name) ||
  stringValue(membership.phone) ||
  fallback

export const subscribeWorkspaceUserDirectory = (
  workspaceId: string,
  onChange: (members: WorkspaceUserDirectoryEntry[]) => void,
  onError?: (error: Error) => void,
) => {
  // Demo workspace short-circuit — no Firestore read, no permission needed.
  // Serves the fixture roster used by Chat + Meetings.
  const demo = getDemoWorkspaceData(workspaceId)
  if (demo) {
    queueMicrotask(() => onChange(demo.members))
    return () => undefined
  }
  let generation = 0
  return onSnapshot(
    query(collection(firestoreDb, 'workspace_members'), where('workspaceId', '==', workspaceId)),
    (snapshot) => {
      const currentGeneration = ++generation
      const rows = snapshot.docs.flatMap((entry) => {
        const data = entry.data() as Record<string, unknown>
        if (['PENDING', 'DECLINED', 'REVOKED'].includes(stringValue(data.status))) return []
        const uid = stringValue(data.uid) || stringValue(data.userId)
        if (!uid) return []
        return [{
          membershipId: entry.id,
          uid,
          membership: data,
        }]
      })

      const uids = rows.map((row) => row.uid)
      void getPublicProfiles(uids).then((profileMap) => {
        if (generation !== currentGeneration) return
        const members: WorkspaceUserDirectoryEntry[] = rows.map((row) => {
          const publicProfile = profileMap.get(row.uid)
          const email = stringValue(row.membership.email) || stringValue(row.membership.user_email)
          const displayName = resolveWorkspaceDirectoryDisplayName(
            row.membership,
            publicProfile,
            fallbackName(row.uid, email),
          )
          const accessScope = row.membership.accessScope as Record<string, unknown> | undefined
          const mode = stringValue(row.membership.mode || row.membership.accessScopeMode || accessScope?.mode) || undefined
          return {
            membershipId: row.membershipId,
            uid: row.uid,
            displayName,
            email,
            role: stringValue(row.membership.role) || 'VIEWER',
            avatarUrl: resolveProfileImageUrl(publicProfile, { photoURL: stringValue(row.membership.photoURL) }),
            mode,
          }
        })
        const seen = new Set<string>()
        const deduped = members.filter((member) => {
          if (seen.has(member.uid)) return false
          seen.add(member.uid)
          return true
        })
        deduped.sort((a, b) => a.displayName.localeCompare(b.displayName))
        onChange(deduped)
      }).catch((error) => {
        if (generation === currentGeneration) onError?.(error instanceof Error ? error : new Error('Could not load workspace members.'))
      })
    },
    (error) => onError?.(error),
  )
}
